import { html, css, nothing } from 'lit';
import type { PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { navigate } from '../router.js';
import { resolveMfaKinds } from '../auth/mfa-kinds.js';
import { MfaKind } from '../api/types.js';
import '../components/sso-card.js';
import '../components/sso-alert.js';
import '../components/sso-spinner.js';

/**
 * `<choose-method-page>` — pick TOTP vs passkey when both are allowed (D6, SSO-MOCKUPS.md §2).
 * Skipped by the server when exactly one method exists. Passkey is shown first (recommended).
 * Also offers "use a recovery code" and "cancel / sign out".
 */
@customElement('choose-method-page')
@localized()
export class ChooseMethodPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .methods {
        display: grid;
        gap: 0.75rem;
        margin: var(--sso-space) 0;
      }
      button.method {
        display: flex;
        align-items: center;
        gap: 0.9rem;
        width: 100%;
        text-align: start;
        padding: 0.9rem 1rem;
        min-height: var(--sso-touch-target);
        background: var(--sso-color-surface);
        border: 1px solid var(--sso-color-border);
        border-radius: var(--sso-radius);
        cursor: pointer;
        color: var(--sso-color-fg);
        font: inherit;
      }
      button.method:hover {
        border-color: var(--sso-color-brand);
      }
      button.method:focus-visible {
        outline: none;
        box-shadow: var(--sso-focus-ring);
      }
      .ico {
        font-size: 1.6rem;
        flex: none;
      }
      .meta {
        flex: 1;
      }
      .meta .title {
        font-weight: 600;
      }
      .meta .desc {
        color: var(--sso-color-muted);
        font-size: 0.9rem;
      }
      .chev {
        color: var(--sso-color-muted);
      }
      .links {
        display: grid;
        gap: 0.5rem;
        margin-top: var(--sso-space);
        font-size: 0.9rem;
      }
    `,
  ];

  /** Distinct method kinds available to this user (Passkey / Totp). */
  @state() private kinds?: MfaKind[];
  @state() private error = '';

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    // Reuse the methods the password step returned (cached on the shell); otherwise ask the server.
    try {
      this.kinds = await resolveMfaKinds(this.flowContext);
      this.autoAdvance();
    } catch {
      this.error = msg('Could not load your sign-in options. Please try again.');
    }
  }

  /** If only one method exists, jump straight to it (the server normally does this). */
  private autoAdvance(): void {
    if (this.kinds && this.kinds.length === 1) this.pick(this.kinds[0]);
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    // Content loads asynchronously — focus the first method button once it has rendered.
    this.focusFirstControl();
  }

  private pick(kind: MfaKind): void {
    navigate(kind === MfaKind.Passkey ? '/2fa/passkey' : '/2fa/totp');
  }

  private useRecovery(): void {
    navigate('/2fa/totp?recovery=1');
  }

  private cancel(): void {
    navigate('/logout');
  }

  private renderMethod(kind: MfaKind) {
    const isPasskey = kind === MfaKind.Passkey;
    return html`
      <button class="method" type="button" @click=${() => this.pick(kind)}>
        <span class="ico" aria-hidden="true">${isPasskey ? '🔑' : '🔢'}</span>
        <span class="meta">
          <span class="title">${isPasskey ? msg('Passkey') : msg('Authenticator code')}</span>
          <span class="desc">
            ${isPasskey
              ? msg('Fingerprint, face or a security key')
              : msg('6-digit code from your app')}
          </span>
        </span>
        <span class="chev" aria-hidden="true">›</span>
      </button>
    `;
  }

  override render() {
    if (!this.kinds && !this.error) {
      return html`<sso-card><sso-spinner aria-label=${msg('Loading')}></sso-spinner></sso-card>`;
    }
    // Passkey first (recommended), then TOTP.
    const ordered = [...(this.kinds ?? [])].sort((a, b) =>
      a === MfaKind.Passkey ? -1 : b === MfaKind.Passkey ? 1 : 0,
    );
    return html`
      <sso-card>
        <h1>${msg('Choose your second step')}</h1>
        <p>${msg('How would you like to verify?')}</p>
        ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
        <div class="methods">${ordered.map((m) => this.renderMethod(m))}</div>
        <div class="links">
          <a href="#" @click=${(e: Event) => { e.preventDefault(); this.useRecovery(); }}>
            ${msg('Use a recovery code instead')}
          </a>
          <a href="#" @click=${(e: Event) => { e.preventDefault(); this.cancel(); }}>
            ${msg('Cancel / Sign out')}
          </a>
        </div>
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'choose-method-page': ChooseMethodPage;
  }
}
